import type { components } from "./schema";

import { apiClient } from "./client";

export type StoreDTO = components["schemas"]["StoreDTO"];
export type StoresResponse = components["schemas"]["StoresResponse"];
export type GatewayDTO = components["schemas"]["GatewayDTO"];
export type GatewaysResponse = components["schemas"]["GatewaysResponse"];
// `is_bound` polje je dodato 2026-05-01 u backend; schema.ts može biti out-of-sync
// dok se OpenAPI ne regeneriše, pa proširujemo ručno.
// Online/battery polja dodata 2026-05-04 (findByMac 6.13).
export type TagDTO = components["schemas"]["TagDTO"] & {
  is_bound?: boolean;
  is_online?: boolean | null;
  battery_percent?: number | null;
  rssi_dbm?: number | null;
  last_update?: string | null;
  firmware?: string | null;
};
export type TagsResponse = components["schemas"]["TagsResponse"];

export type TagState = "all" | "bound" | "unbound";
// `binds_available` i `upstream_healthy` polja dodata 2026-05-04 u backend; schema.ts
// može biti out-of-sync dok se OpenAPI ne regeneriše, pa proširujemo ručno.
export type StoreTagSummary = components["schemas"]["StoreTagSummary"] & {
  binds_available?: boolean;
};
export type TagsOverviewResponse = components["schemas"]["TagsOverviewResponse"] & {
  upstream_healthy?: boolean;
};

export async function listStores(): Promise<StoresResponse> {
  const { data } = await apiClient.get<StoresResponse>("/api/stores");
  return data;
}

export interface StoreCreateRequest {
  number: string;
  name: string;
  address: string;
  /** Default true — backend auto-purge-uje cloud-kopirane product-e posle create-a. */
  clean?: boolean;
}

export interface StoreCreateResponse {
  id: string;
  number: string;
  name: string;
  address: string;
  cleaned_products: number;
  seeded_products: number;
  seed_failures: number;
  message: string;
}

export async function createStore(
  payload: StoreCreateRequest,
): Promise<StoreCreateResponse> {
  const { data } = await apiClient.post<StoreCreateResponse>("/api/stores", payload, {
    // Auto-clean može trajati 60-90s za 12K+ kopiranih product-a
    timeout: 300_000,
  });
  return data;
}

export interface StoreDeleteResponse {
  id: string;
  message: string;
}

export interface StoreContents {
  store_id: string;
  products: number;
  tags: number;
  gateways: number;
  templates: number;
  can_force_delete: boolean;
  notes: string[];
}

export async function getStoreContents(storeId: string): Promise<StoreContents> {
  const { data } = await apiClient.get<StoreContents>(
    `/api/stores/${encodeURIComponent(storeId)}/contents`,
  );
  return data;
}

export interface GatewayCreateRequest {
  mac: string;
  name: string;
}

export interface GatewayCreateResponse {
  store_id: string;
  mac: string;
  name: string;
  message: string;
}

export interface GatewayUpdateResponse {
  store_id: string;
  id: string;
  name: string;
  message: string;
}

export interface GatewayDeleteResponse {
  store_id: string;
  id: string;
  message: string;
}

export async function addGateway(
  storeId: string,
  payload: GatewayCreateRequest,
): Promise<GatewayCreateResponse> {
  const { data } = await apiClient.post<GatewayCreateResponse>(
    `/api/stores/${encodeURIComponent(storeId)}/gateways`,
    payload,
  );
  return data;
}

export async function renameGateway(
  storeId: string,
  gatewayId: string,
  name: string,
): Promise<GatewayUpdateResponse> {
  const { data } = await apiClient.patch<GatewayUpdateResponse>(
    `/api/stores/${encodeURIComponent(storeId)}/gateways/${encodeURIComponent(gatewayId)}`,
    { name },
  );
  return data;
}

export async function deleteGateway(
  storeId: string,
  gatewayId: string,
): Promise<GatewayDeleteResponse> {
  const { data } = await apiClient.delete<GatewayDeleteResponse>(
    `/api/stores/${encodeURIComponent(storeId)}/gateways/${encodeURIComponent(gatewayId)}`,
  );
  return data;
}

export async function deleteStore(
  storeId: string,
  opts: { force?: boolean } = {},
): Promise<StoreDeleteResponse & { purged?: { products: number; tags: number; gateways: number } }> {
  const { data } = await apiClient.delete<
    StoreDeleteResponse & { purged?: { products: number; tags: number; gateways: number } }
  >(`/api/stores/${encodeURIComponent(storeId)}`, {
    params: { force: opts.force ? "true" : undefined },
    // Force purge može trajati nekoliko minuta za 10K+ product-a (200 po batch-u,
    // svaki ~200ms latency). 5min cap ostavlja prostor za realne use-case-ove.
    timeout: opts.force ? 300_000 : 30_000,
  });
  return data;
}

export async function listStoreGateways(storeId: string): Promise<GatewaysResponse> {
  const { data } = await apiClient.get<GatewaysResponse>(
    `/api/stores/${encodeURIComponent(storeId)}/gateways`,
  );
  return data;
}

export async function listStoreTags(
  storeId: string,
  opts: {
    page?: number;
    size?: number;
    includeDetails?: boolean;
    state?: TagState;
  } = {},
): Promise<TagsResponse> {
  const { data } = await apiClient.get<TagsResponse>(
    `/api/stores/${encodeURIComponent(storeId)}/tags`,
    {
      params: {
        page: opts.page ?? 1,
        size: opts.size ?? 50,
        include_details: opts.includeDetails ?? true,
        state: opts.state ?? "all",
      },
    },
  );
  return data;
}

export async function getTagsOverview(
  includeWarnings = true,
): Promise<TagsOverviewResponse> {
  const { data } = await apiClient.get<TagsOverviewResponse>(
    "/api/stores/tags-overview",
    { params: { include_warnings: includeWarnings } },
  );
  return data;
}
